import { CircleAlert, CircleX, FileText, Image, Video, Volume2 } from 'lucide-react'
import { ReactNode, type FC } from 'react'
import { webchatClasses } from '../../../../styles'
import { useTranslations } from '../../../../contexts/TranslationContext'
import type { FileType } from '../../../../types'

type FileAttachmentProps = {
  name: string
  type: FileType
  instance: File
  status: string
  removeFile: (name: string) => void
}

export const FileAttachment: FC<FileAttachmentProps> = ({ name, type, instance, status, removeFile }) => {
  const { loader, fileIcon, removeIcon, errorIcon, previewImage } = webchatClasses.composer.fileWidget
  const t = useTranslations()
  const extension = getExtension(instance.name, t['fileAttachment.fallbackExtension'])
  const isImage = ['PNG', 'JPEG', 'JPG'].includes(extension)

  if (status === 'loading') {
    return (
      <FileCard name={name} status={status} removeButton={<div {...loader} />} extension={extension}>
        <div {...fileIcon.container}>
          <FileIcon type={type} {...fileIcon.icon} />
        </div>
      </FileCard>
    )
  }

  if (status === 'error') {
    return (
      <FileCard
        name={name}
        status={status}
        removeButton={<RemoveButton removeFile={() => removeFile(name)} />}
        extension={t['fileAttachment.uploadError']}
      >
        <div {...fileIcon.container}>
          <CircleAlert {...errorIcon} />
          <FileIcon type={type} {...fileIcon.icon} />
        </div>
      </FileCard>
    )
  }

  if (status == 'uploaded' && isImage) {
    return (
      <FileCard
        name={name}
        status={status}
        removeButton={<RemoveButton removeFile={() => removeFile(name)} />}
        extension={extension}
      >
        <div {...fileIcon.container}>
          <img src={URL.createObjectURL(instance)} alt={name} {...previewImage} />
        </div>
      </FileCard>
    )
  }

  return (
    <FileCard
      name={name}
      status={status}
      removeButton={<CircleX onClick={() => removeFile(name)} {...removeIcon} />}
      extension={extension}
    >
      <div {...fileIcon.container}>
        <FileIcon type={type} {...fileIcon.icon} />
      </div>
    </FileCard>
  )
}

type FileCardProps = {
  name: string
  status: string
  removeButton: ReactNode
  children: ReactNode
  extension: string
}

const FileCard: FC<FileCardProps> = ({ name, status, removeButton, children, extension }) => {
  const { container, metadata } = webchatClasses.composer.fileWidget
  return (
    <div data-status={status} title={name} {...container}>
      {removeButton}
      {children}
      <div {...metadata.container}>
        <div {...metadata.name}>{name}</div>
        <div {...metadata.type}>{extension}</div>
      </div>
    </div>
  )
}

const FileIcon: FC<{ type: string; className: string }> = ({ type, ...props }) => {
  if (type === 'image') return <Image {...props} />
  if (type === 'video') return <Video {...props} />
  if (type === 'audio') return <Volume2 {...props} />
  return <FileText {...props} />
}

const getExtension = (name: string | undefined, fallback: string) => {
  if (!name) return fallback
  const extension = name.split('.').pop()
  return extension ? extension.toUpperCase() : fallback
}

const RemoveButton: FC<{ removeFile: () => void }> = ({ removeFile }) => {
  const {
    composer: {
      fileWidget: { removeIcon },
    },
  } = webchatClasses

  const t = useTranslations()
  return (
    <CircleX
      role="button"
      tabIndex={0}
      aria-label={t['fileAttachment.removeButton.ariaLabel']}
      onClick={() => removeFile()}
      {...removeIcon}
    />
  )
}
